import { ApolloProvider } from '@apollo/client';
import { EmbeddableVirtualConnectionTableSubplaceWithoutRouter } from '@tableau/data-catalog-client-components';
import { SiteUrlsContext } from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { CatalogApiRequests } from '~src/catalog/CatalogApiRequests';
import { catalogApolloClient } from '~src/catalog/CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from '~src/catalog/CatalogFeatureFlagsAdapter';
import { getTelemetryCommonProperties } from '~src/catalog/DataCatalogTelemetry';
import { DataCatalogUIActionsService } from '~src/catalog/services/DataCatalogUIActionsService';
import { Messages } from '~src/codegen/Localize';
import { IPublishedConnectionDetailInfo } from '~src/publishedConnection/models/IPublishedConnectionInfo';
import { PublishedConnectionTableDefinition } from '~src/publishedConnectionTable/contentDefinitions/PublishedConnectionTableDefinition';
import { IPublishedConnectionTableDetailInfo, IPublishedConnectionTableInfo } from '~src/publishedConnectionTable/models/IPublishedConnectionTableInfo';
import { IPublishedConnectionTableActions } from '~src/publishedConnectionTable/serverApi/types/IPublishedConnectionTableActions';
import { populateContentBreadcrumbs } from '~src/spacesPlatform/components/breadcrumbs/BreadcrumbHelpers';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { maybeSendTelemetry_favoriteItemOpened } from '~src/spacesPlatform/components/spaceComponents/SpaceTelemetry';
import { explorerSubspace } from '~src/spacesPlatform/components/spaceComponents/SubspaceHelpers';
import { Subspace, TabbedSpace } from '~src/spacesPlatform/components/spaceComponents/TabbedSpace';
import { IExplorableItemTypeInfo, INamedItemInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { ISpaceInfoTypeItemCount } from '~src/spacesPlatform/state/ContentCountsState';
import { PublishedConnectionTableSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';

import { PublishedConnectionTableSubplaceProps } from '../types/PublishedConnectionTableSubplaceProps';
import { PublishedConnectionTableSpaceHeader } from './PublishedConnectionTableSpaceHeader';

export type PublishedConnectionTableSpaceStateProps = {
  readonly contentCountsPublishedConnectionTable: ISpaceInfoTypeItemCount<'virtualConnectionTable'>;
  readonly contentCountsUpToDate: boolean;
  readonly urls: SpacesSiteUrls;
  readonly catalogingOnSiteEnabled: boolean;
  readonly areUsersVisible: boolean;
};

export type PublishedConnectionTableSpaceDispatchProps = {
  readonly addToFavorites: () => void;
  readonly enterSpace: (requestContent: boolean) => void;
  readonly fetchUpdatedContentCounts: () => void;
  readonly markContentCountsUpToDate: () => void;
  readonly removeFromFavorites: () => void;
};

export interface PublishedConnectionTableSpaceOwnProps {
  readonly publishedConnectionDetailInfo: IPublishedConnectionDetailInfo;
  readonly publishedConnectionTableAllowedActions: IPublishedConnectionTableActions;
  readonly publishedConnectionTableDetailInfo: IPublishedConnectionTableDetailInfo;
  readonly publishedConnectionTableInfo: IPublishedConnectionTableInfo;
  readonly projectHierarchy: INamedItemInfo[];

  readonly refreshData: () => void;

  // subplace info
  readonly subplaceName: string;
  readonly siteUrls: SiteUrls;
}

export type PublishedConnectionTableSpaceProps =
  PublishedConnectionTableSpaceStateProps
  & PublishedConnectionTableSpaceDispatchProps
  & PublishedConnectionTableSpaceOwnProps;

function overviewSubspace(subplaceProps: PublishedConnectionTableSubplaceProps): Subspace {
  return explorerSubspace({
    contentCount: getContentCount(subplaceProps.contentCountsPublishedConnectionTable, 'columns'),
    contentCountUpToDate: subplaceProps.contentCountsUpToDate,
    emptyStateProps: {
      primaryMessage: Messages.column_table_columns()
    },
    hideSelection: false,
    name: 'overview',
    shouldRenderContentCount: true,
    supportedDisplayModes: PublishedConnectionTableDefinition.supportedDisplayModes,
    supportFilterPanel: false,
    tabLabel: Messages.column_table_columns(),
    toolbarVisibility: 'enabled',
  });
}

function lineageSubspace(id: string, siteUrls: SiteUrls, areUsersVisible: boolean, siteLuid: string): Subspace {
  return {
    name: 'lineage',
    tabLabel: Messages.dataCatalogLineage(),
    shouldRenderTabContentCount: false,
    renderContent: () =>
      <EmbeddableVirtualConnectionTableSubplaceWithoutRouter
        vizportalUrlId={id}
        client={catalogApolloClient}
        siteUrls={siteUrls}
        apiRequests={CatalogApiRequests}
        uiActions={DataCatalogUIActionsService.instance()}
        areUsersVisible={areUsersVisible}
        featureFlags={catalogFeatureFlagsAdapter()}
        telemetryContext={getTelemetryCommonProperties()}
        siteLuid={siteLuid}
        />
  };
}

function getPublishedConnectionTableDetailWithUsage(
  publishedConnectionTableDetailInfo: IPublishedConnectionTableDetailInfo,
  publishedConnectionTableInfo: IPublishedConnectionTableInfo,
) {
  return {
    usageInfo: publishedConnectionTableInfo.usageInfo,
    usageInfoMaxValues: publishedConnectionTableInfo.usageInfoMaxValues,
    ...publishedConnectionTableDetailInfo,
  };
}

function getPublishedConnectionTableSubspaces(subplaceProps: PublishedConnectionTableSubplaceProps) {
  const siteLuid = SessionSiteSelectors.siteLuid(getAppStore().getState());
  return _.compact([
    overviewSubspace(subplaceProps),
    FeatureFlags.publishedConnectionsLineageEnabled &&
      subplaceProps.catalogingOnSiteEnabled &&
      lineageSubspace(subplaceProps.publishedConnectionTableDetailInfo.id, subplaceProps.siteUrls, subplaceProps.areUsersVisible, siteLuid),
  ]);
}

function getContentCount(contentCountsPublishedConnectionTable: ISpaceInfoTypeItemCount<'virtualConnectionTable'>, contentName: string): number {
  return contentCountsPublishedConnectionTable && contentCountsPublishedConnectionTable[contentName as PublishedConnectionTableSubspaceName];
}

export class PublishedConnectionTableSpace extends React.PureComponent<PublishedConnectionTableSpaceProps> {
  private memoizedPublishedConnectionTableDetailWithUsage = defaultMemoize(getPublishedConnectionTableDetailWithUsage);
  private memoizedGetPublishedConnectionTableSubspaces = defaultMemoize(getPublishedConnectionTableSubspaces);

  public urlFromSubspaceName = (subspaceName?: PublishedConnectionTableSubspaceName) => {
    switch (subspaceName) {
      case 'overview':
        return this.props.urls.virtualConnectionTableOverviewUrl(this.props.publishedConnectionTableDetailInfo.id);
      case 'scheduledTasks':
        return this.props.urls.virtualConnectionTableScheduledTasksUrl(this.props.publishedConnectionTableDetailInfo.id);
      case 'lineage':
        return this.props.urls.virtualConnectionTableLineageUrl(this.props.publishedConnectionTableDetailInfo.id);
    }
  };

  public render() {
    const subplaceProps: PublishedConnectionTableSubplaceProps = {
      contentCountsPublishedConnectionTable: this.props.contentCountsPublishedConnectionTable,
      contentCountsUpToDate: this.props.contentCountsUpToDate,
      fetchUpdatedContentCounts: this.props.fetchUpdatedContentCounts,
      publishedConnectionTableAllowedActions: this.props.publishedConnectionTableAllowedActions,
      publishedConnectionTableDetailInfo: this.props.publishedConnectionTableDetailInfo,
      refreshData: this.props.refreshData,
      siteUrls: this.props.urls,
      areUsersVisible: this.props.areUsersVisible,
      catalogingOnSiteEnabled: this.props.catalogingOnSiteEnabled
    };

    const subspaces = this.memoizedGetPublishedConnectionTableSubspaces(subplaceProps);

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.subplaceName as PublishedConnectionTableSubspaceName}
        urlForSubspace={this.urlFromSubspaceName}
        mastheadElement={this.mastheadElement()}
        testIdPrefix={'virtualConnectionTable'}
        spaceItemName={this.props.publishedConnectionTableDetailInfo.name}
      />
    );
  }

  public componentDidMount() {
    maybeSendTelemetry_favoriteItemOpened(this.props.publishedConnectionTableDetailInfo);
    this.changeSubspace(true);
    this.updateBreadcrumbs();
  }

  public componentDidUpdate(prevProps: PublishedConnectionTableSpaceProps) {
    if (this.props.publishedConnectionTableDetailInfo.id !== prevProps.publishedConnectionTableDetailInfo.id) {
      maybeSendTelemetry_favoriteItemOpened(this.props.publishedConnectionTableDetailInfo);
      this.changeSubspace(true);
      this.updateBreadcrumbs();
    } else if (this.props.subplaceName !== prevProps.subplaceName) {
      this.changeSubspace(false);
    }
  }

  private shouldRequestContent() {
    // tasks are fetched from within the component, so no need to request via mixed content action
    switch (this.props.subplaceName) {
      case 'overview':
        return true;
      case 'scheduledTasks':
        return false;
    }
  }

  private changeSubspace(didPublishedConnectionTableChange: boolean) {
    this.props.enterSpace(this.shouldRequestContent());
    if (didPublishedConnectionTableChange) {
      this.props.fetchUpdatedContentCounts();
    } else {
      // enterSpace action causes counts out of date.
      // We mark it upto date because there is no need to fetch count again for same published connection.
      this.props.markContentCountsUpToDate();
    }
  }

  private updateBreadcrumbs() {
    const publishedConnection: IExplorableItemTypeInfo<'virtualConnection'> = {
      id: this.props.publishedConnectionDetailInfo.id,
      name: this.props.publishedConnectionDetailInfo.name,
      contentType: 'virtualConnection'
    };
    populateContentBreadcrumbs([publishedConnection, this.props.publishedConnectionTableDetailInfo], this.props.urls);
  }

  private mastheadElement(): React.ReactNode {
    const publishedConnectionTableDetailWithUsage = this.memoizedPublishedConnectionTableDetailWithUsage(
      this.props.publishedConnectionTableDetailInfo,
      this.props.publishedConnectionTableInfo,
    );
    const siteLuid = SessionSiteSelectors.siteLuid(getAppStore().getState());

    return (
      <ApolloProvider client={catalogApolloClient}>
        <SiteUrlsContext.Provider value={this.props.siteUrls}>
          <PublishedConnectionTableSpaceHeader
            itemInfo={publishedConnectionTableDetailWithUsage}
            availableActions={this.props.publishedConnectionTableAllowedActions}
            openDetail={() => null}
            addToFavorites={this.props.addToFavorites}
            removeFromFavorites={this.props.removeFromFavorites}
            urls={this.props.urls}
            catalogingOnSiteEnabled={this.props.catalogingOnSiteEnabled}
          />
        </SiteUrlsContext.Provider>
      </ApolloProvider>
    );
  }
}
